import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, ViewChild, inject } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IrisFieldBaseComponent } from '@iris/common/modules/fields/field-base/field-base.component';
import { StrakitStylingControlDirective } from '@ngx-strakit/cdk';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';

@Component({
  selector: 'iris-field-chips',
  templateUrl: 'field-chips.component.html',
  styleUrls: ['field-chips.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IrisFieldChipsComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [{
    directive: StrakitStylingControlDirective,
    inputs: ['stylingMode'],
  }],
  standalone: false,
  host: {
    '[class.has-label]': '!!this.label',
  },
})
export class IrisFieldChipsComponent extends IrisFieldBaseComponent implements ControlValueAccessor {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly enhancedControl = inject(StrakitStylingControlDirective, { host: true });

  @Input() visible: true;
  @Input() disabled = false;
  @Input() removable: true;
  @Input() addOnBlur: true;
  @ViewChild('input') input: ElementRef;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  constructor() {
    super();
    const globalSandbox = inject(IrisGlobalSandbox);
    this.enhancedControl.stylingMode.set(globalSandbox.theme()?.strabagControlsEnabled ? 'automatized' : 'underlined');
  }

  add(event: MatChipInputEvent): void {
    const input = event.chipInput.inputElement;
    const value = event.value;

    // Add our item
    if ((value || '').trim()) {
      this.valueControl.setValue(
        [...(this.valueControl.value || []), value.trim()],
      );
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  clearChips(): void {
    this.valueControl.reset();
    this.input.nativeElement.value = '';
    this.changeDetectorRef.markForCheck();
  }

  remove(item: string): void {
    const index = this.valueControl.value.indexOf(item);
    const val = [...this.valueControl.value];

    if (index >= 0) {
      val.splice(index, 1);
      this.valueControl.setValue(val);
    }
  }
}
